import React from 'react';
import { motion } from 'framer-motion';
import GlassCard from '../components/UI/GlassCard';
import CyberButton from '../components/UI/CyberButton';
import HUDElement from '../components/UI/HUDElement';
import NeonText from '../components/UI/NeonText';
import ServicesGrid from '../components/Sections/ServicesGrid';
import { useScrollAnimation, scrollVariants, staggerContainer } from '../hooks/useScrollAnimation';

const Services = () => {
    const { ref: heroRef, controls: heroControls } = useScrollAnimation();
    const { ref: statsRef, controls: statsControls } = useScrollAnimation();

    const serviceStats = [
        { value: 150, label: 'Proyectos Completados', suffix: '+' },
        { value: 99.8, label: 'Satisfacción del Cliente', suffix: '%' },
        { value: 24, label: 'Soporte Continuo', suffix: '/7' },
        { value: 50, label: 'Expertos en Tecnología', suffix: '+' }
    ];

    const processSteps = [
        {
            step: '01',
            title: 'Consulta & Análisis',
            description: 'Evaluación detallada de requisitos y objetivos del proyecto.',
            technologies: ['IA Analysis', 'Requirement Mapping', 'Feasibility Study']
        },
        {
            step: '02',
            title: 'Diseño & Arquitectura',
            description: 'Creación de arquitectura técnica y diseño de interfaz.',
            technologies: ['System Architecture', 'UI/UX Design', 'Prototyping']
        },
        {
            step: '03',
            title: 'Desarrollo & Implementación',
            description: 'Codificación y implementación de soluciones tecnológicas.',
            technologies: ['Agile Development', 'CI/CD', 'Quality Assurance']
        },
        {
            step: '04',
            title: 'Despliegue & Soporte',
            description: 'Lanzamiento y mantenimiento continuo del sistema.',
            technologies: ['Deployment', 'Monitoring', 'Technical Support']
        }
    ];

    return (
        <div className="services-page">
            {/* Hero Section */}
            <motion.section
                className="page-hero"
                ref={heroRef}
                initial="hidden"
                animate={heroControls}
                variants={scrollVariants}
            >
                <div className="container">
                    <div className="hero-content">
                        <NeonText variant="cyan" size="large" animated={true}>
                            // SERVICIOS_TECNOLÓGICOS
                        </NeonText>
                        <h1 className="hero-title">
                            Soluciones Avanzadas
                            <span className="accent"> para el Futuro Digital</span>
                        </h1>
                        <p className="hero-description">
                            Desarrollamos tecnologías de vanguardia que transforman ideas
                            en realidades digitales. Nuestros sistemas están diseñados para
                            superar los límites de lo posible.
                        </p>

                        <div className="hero-actions">
                            <CyberButton variant="cyan" size="large">
                                Iniciar Proyecto
                            </CyberButton>
                            <CyberButton variant="outline" size="large">
                                Ver Casos de Estudio
                            </CyberButton>
                        </div>
                    </div>

                    <div className="hero-stats">
                        {serviceStats.map((stat, index) => (
                            <motion.div
                                key={stat.label}
                                className="stat-item"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 + 0.5 }}
                            >
                                <HUDElement
                                    label={stat.label}
                                    value={stat.value}
                                    unit={stat.suffix}
                                    variant="compact"
                                />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.section>

            {/* All Services Grid */}
            <section className="section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <NeonText variant="magenta" size="large">
                            // CATALOGO_SERVICIOS
                        </NeonText>
                        <h2>Tecnologías Especializadas</h2>
                        <p>Soluciones completas desde la concepción hasta la implementación</p>
                    </motion.div>

                    <ServicesGrid />
                </div>
            </section>

            {/* Process Section */}
            <section className="section process-section">
                <div className="container">
                    <motion.div
                        className="section-header"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <NeonText variant="cyan" size="large">
                            // PROCESO_DESARROLLO
                        </NeonText>
                        <h2>Metodología de Implementación</h2>
                        <p>Un enfoque sistemático para garantizar el éxito de cada proyecto</p>
                    </motion.div>

                    <motion.div
                        className="process-grid"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {processSteps.map((step, index) => (
                            <motion.div key={step.step} variants={scrollVariants} className="process-step">
                                <GlassCard hover={false}>
                                    <div className="process-step__header">
                                        <span className="step-number">{step.step}</span>
                                        <h3 className="step-title">{step.title}</h3>
                                    </div>
                                    <p className="step-description">{step.description}</p>
                                    <div className="step-technologies">
                                        {step.technologies.map((tech, techIndex) => (
                                            <span key={techIndex} className="tech-tag">
                        {tech}
                      </span>
                                        ))}
                                    </div>
                                    <div className="step-connector">
                                        {index < processSteps.length - 1 && (
                                            <div className="connector-line"></div>
                                        )}
                                    </div>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section cta-section">
                <div className="container">
                    <GlassCard className="cta-card">
                        <div className="cta-content">
                            <NeonText variant="magenta" size="large">
                                // INICIAR_COLABORACION
                            </NeonText>
                            <h2>¿Listo para Desarrollar el Futuro?</h2>
                            <p>
                                Nuestro equipo de especialistas está preparado para llevar
                                tu proyecto al siguiente nivel. Contáctanos para una consulta
                                gratuita y un análisis detallado.
                            </p>
                            <div className="cta-actions">
                                <CyberButton variant="cyan" size="large" to="/contact">
                                    Solicitar Consulta
                                </CyberButton>
                                <CyberButton variant="outline" size="large">
                                    Descargar Brochure
                                </CyberButton>
                            </div>
                        </div>
                    </GlassCard>
                </div>
            </section>
        </div>
    );
};

export default Services;